import { useCallback, useEffect, useMemo, useState } from "react";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import live from "@/data/breakageBoard.json";
import { toTiles, extraTiles } from "./live";
import { buildLiveSummary } from "./live";
import { SUMMARY_SEPARATOR } from "@/lib/buildSummary";
import { BreakageBoard, AsOfStamp } from "./BreakageBoard";
import { SummaryStripBase, Card, Label, StateWord } from "@/design/primitives";
import { TileDetail } from "./TileDetail";
import { useBoard } from "@/hooks/useBoard";
import { StartHere, AddWorkBar, AuthGate } from "@/screens/cc";
import { STATE_WORD } from "./states";

// Bare wall, interactive: same-tile contracts (slug/name/brand/state/line/number/label/
// stamp/track/detail) hydrated live from the action API; baked committed data renders
// whenever the API isn't reachable. Jeff's verbs act on real work: take it / false alarm.

const clock = (module) => {
  const as_of = module?.stamp?.ingested_at;
  if (!as_of) return { asOf: "unknown", fresh: false, hours: NaN };
  const hours = (Date.now() - new Date(as_of).getTime()) / 36e5;
  return {
    asOf: new Date(as_of).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }),
    fresh: hours <= 30,
    hours,
    dead: hours > 48,
  };
};

// Jeff's recorded call on a tile, as the board should say it (plain English, no verbs of the system).
const jeffLine = (tile) => {
  const j = tile.jeff || {};
  if (j.kitTaken) return "You sent it to Kit — it's on Kit's desk now";
  if (j.dismissed) return "You called it: that one's handled";
  return null;
};

const LiveWall = ({ desktop }) => {
  const [openSlug, setOpenSlug] = useState(null);
  const [flash, setFlash] = useState(null);
  const board = useBoard("breakage");

  const bakedTiles = useMemo(() => [...toTiles(live), ...extraTiles(live)], []);
  const c = useMemo(() => clock(live), []);
  const stale = !c.fresh;
  const close = useCallback(() => setOpenSlug(null), []);

  const tiles = useMemo(() => {
    if (!board.live) return bakedTiles;
    // Hydrate: match live rows onto baked tiles by slug; the payload carries the jeff overlay.
    const rows = new Map((board.items || []).filter((r) => r.kind === "tile").map((r) => [r.slug, r]));
    return bakedTiles.map((t) => {
      const row = rows.get(t.slug);
      if (!row) return { ...t, jeff: {} };
      const p = row.payload || {};
      const j = p.jeff || {};
      const renamedTo = p.jeff?.renamed?.to;
      const editedWhy = p.jeff?.editedText?.to;
      return {
        ...t,
        name: renamedTo || p.title || t.name,
        jeff: j,
        line: jeffLine({ jeff: j }) || (editedWhy ? editedWhy : t.line),
        state: j.kitTaken && t.state === "waiting" ? "stale" : t.state, // acted-on tiles leave the waiting tone
        stamp: j.kitTaken
          ? `sent to Kit ${new Date(j.kitTaken.at).toLocaleString([], { month: "short", day: "numeric" })}`
          : j.dismissed
            ? `sorted ${new Date(j.dismissed.at).toLocaleString([], { month: "short", day: "numeric" })}`
            : t.stamp,
        detail: {
          ...t.detail,
          does: renamedTo || p.title || t.detail.does,
          why: editedWhy || p.why || t.detail.why,
        },
      };
    });
  }, [board.live, board.items, bakedTiles]);

  const summary = useMemo(() => buildLiveSummary(tiles), [tiles]);
  const openItem = tiles.find((t) => t.slug === openSlug);

  // Start here: the renewal click is the one real action waiting on Jeff on this wall.
  const stall = tiles.find((t) => t.state === "waiting" && !(t.jeff || {}).kitTaken && !(t.jeff || {}).dismissed);
  const startHere = !board.live
    ? {
        tone: "muted",
        text: board.status === "needs-signin"
          ? "Sign in to work the board — your buttons appear the moment you're in"
          : "Actions are offline this visit — the wall shows the latest committed state",
      }
    : stall
      ? {
          tone: "amber",
          text: `One thing is waiting on you: your call on "${stall.name}" — one click sends it to Kit`,
          button: {
            label: board.pending === stall.slug ? "Sending…" : "Send to Kit",
            disabled: Boolean(board.pending),
            onClick: async () => {
              const res = await board.act("take", stall.slug, "Jeff sent this to Kit from the wall");
              if (res) setFlash("Sent — Kit picks it up and the wall stops asking.");
            },
          },
        }
      : { tone: "green", text: "Nothing on the wall needs you — everything else is already Kit's problem." };

  const renderWall = (children) => (
    <div data-testid="breakage-live" data-stale={stale ? "1" : "0"} className="w-full font-inter text-os-ink">
      <div className={desktop ? "mx-auto flex max-w-[100rem] flex-col gap-4 px-10 py-6" : "flex flex-col gap-3 px-4 pb-8 pt-4"}>
        {children}
      </div>
    </div>
  );

  if (board.status === "needs-signin") {
    return renderWall(
      <>
        <AuthGate />
        <SummaryStripBase
          {...summary}
          separator={SUMMARY_SEPARATOR}
          size={desktop ? "lg" : "sm"}
          meta={<AsOfStamp asOf={c.asOf} fresh={!stale} />}
        />
        <div className={desktop ? "grid auto-rows-fr grid-cols-3 gap-4" : "flex flex-col gap-3"}>
          {bakedTiles.map((tile) => (
            <TileButton key={tile.slug} tile={tile} onOpen={setOpenSlug} />
          ))}
        </div>
      </>,
    );
  }

  const actedSlugs = tiles.filter((t) => (t.jeff || {}).kitTaken || (t.jeff || {}).dismissed);

  return renderWall(
    <>
      <StartHere {...startHere} testId="breakage-start-here" />
      {flash && (
        <p data-testid="breakage-flash" className="text-[0.875rem] font-medium text-os-green">
          {flash}
        </p>
      )}
      <SummaryStripBase
        {...summary}
        separator={SUMMARY_SEPARATOR}
        size={desktop ? "lg" : "sm"}
        meta={
          stale ? (
            <div
              data-testid="breakage-stale-banner"
              className="shrink-0 whitespace-nowrap pt-1 text-[0.75rem] uppercase tracking-[0.08em]"
              style={{ color: c.dead ? "var(--os-red)" : "var(--os-amber)" }}
            >
              {c.dead ? "board stale — not checking right now" : `board info from ${c.asOf} — checking twice a day`}
            </div>
          ) : (
            <AsOfStamp asOf={c.asOf} fresh />
          )
        }
      />
      <div className={desktop ? "grid auto-rows-fr grid-cols-3 gap-4" : "flex flex-col gap-3"}>
        {tiles.map((tile) => (
          <TileButton key={tile.slug} tile={tile} onOpen={setOpenSlug} />
        ))}
      </div>
      {actedSlugs.length > 0 && (
        <Card quiet className="px-5 py-3">
          <Label as="p">
            Your calls recorded on this wall: {actedSlugs.map((t) => t.name).join(" · ")}
          </Label>
        </Card>
      )}
      {board.live && <AddWorkBar />}
      {openItem && (
        <TileDetail
          item={openItem}
          variant={desktop ? "desktop" : "mobile"}
          onClose={close}
          editor={board.live
            ? { board, item: (board.items || []).find((r) => r.slug === openSlug) }
            : null}
        />
      )}
    </>,
  );
};

const TileButton = ({ tile, onOpen }) => (
  <button
    type="button"
    data-testid={`tile-${tile.slug}`}
    data-state={tile.state}
    onClick={() => onOpen(tile.slug)}
    className="cursor-pointer text-left"
  >
    <WatchTileLive tile={tile} />
  </button>
);

// thin re-render of WatchTile for live tiles (same primitives; stamp/line from adapter)
import { MonoMeta, Track, WaitingChip } from "@/design/primitives";
const WatchTileLive = ({ tile }) => {
  const w = STATE_WORD[tile.state];
  return (
    <Card quiet={tile.state === "empty"} data-testid={`live-tile-${tile.slug}`} data-state={tile.state} className="flex h-full flex-col p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="min-w-0 truncate text-[0.9375rem] font-semibold tracking-[-0.01em] text-os-ink">{tile.name}</h3>
        <StateWord tone={w.tone}>{w.word}</StateWord>
      </div>
      <Label as="p" className="mt-1">{tile.brand}</Label>
      {tile.state === "waiting" ? (
        <div className="mt-3 flex-1"><WaitingChip>{tile.line}</WaitingChip></div>
      ) : (
        <p className="mt-3 flex-1 text-[0.875rem] leading-[1.45] text-os-ink">{tile.line}</p>
      )}
      <div className="mt-4 border-t border-os-border pt-4">
        <div>
          <div className="text-[2.875rem] font-semibold leading-none tracking-[-0.035em] text-os-ink tabular-nums">{tile.number}</div>
          <Label as="div" className="mt-2">{tile.label}</Label>
        </div>
        {tile.track ? <Track runs={tile.track} emptyLabel="no runs yet" className="mt-4" /> : null}
      </div>
      <MonoMeta as="p" className="mt-3">{tile.stamp}</MonoMeta>
    </Card>
  );
};

export default function LiveBreakagePage() {
  const desktop = useMediaQuery("(min-width: 1024px)");
  return <LiveWall desktop={desktop} />;
}